import type { ComponentChildren } from 'preact';
import { versionedUrl } from '../../utils/versionedUrl';
import { useMe, isAdmin, signOut } from '../../auth/useMe';
import type { MeState } from '../../auth/useMe';
import type { CurrentUser } from '../../auth/types';
import { useTranslation } from '../../i18n';

/**
 * Shared sticky platform navbar. In the unified SPA the marketing landing, emporium catalog,
 * and curator console all live on the same origin - there's no cross-subdomain composition to
 * coordinate, so this navbar is the trimmed-down version of the legacy multi-app variant.
 *
 * Three-column grid `[brand] [middle] [end]`:
 *   • Left column auto-sizes to the brand row.
 *   • Middle column is `1fr` and receives `middleSlot` (typically PlatformPills).
 *   • End column auto-sizes; `actions` render here.
 *
 * Deferred from the legacy port:
 *   • Theme switcher - the new SPA serves its own tokens at build time, no runtime swap yet.
 *   • Language switcher - locale URL helpers haven't been ported, switcher lands when auth
 *     and the locale-aware admin chrome do.
 *   • Auth user chip + admin pill - auth surface is deferred.
 *   • `useLocaleUrls()` - same reason; brand `homeHref` defaults to `/`.
 */
export interface NavbarBrandConfig {
    /**
     * Path segments rendered after "Gposingway", each prefixed with a `/`. Used to grow the
     * brand line into a breadcrumb - `path: ['Emporium']` reads "Gposingway / Emporium".
     */
    path?: string[];
    /** Version chip after the wordmark. Mono uppercase (e.g. "v8.3 · 7.3"). */
    version?: string;
}

export interface NavbarProps {
    brand?: NavbarBrandConfig;
    /** Override the brand link target. Default: `/`. */
    homeHref?: string;
    /** Aria-label for the brand link. Default: "Gposingway home". */
    homeAriaLabel?: string;
    /** Content rendered in the center column. */
    middleSlot?: ComponentChildren;
    /** Content rendered in the trailing actions slot. */
    actions?: ComponentChildren;
}

export function Navbar({
    brand,
    homeHref = '/',
    homeAriaLabel = 'Gposingway home',
    middleSlot,
    actions,
}: NavbarProps) {
    return (
        <nav class="sticky top-0 z-50 border-b border-rule bg-surface-veil backdrop-blur-xl">
            <div class="mx-auto grid max-w-[1680px] grid-cols-[auto_1fr_auto] items-center gap-6 px-[clamp(20px,3.5vw,56px)] py-3">
                <a
                    href={homeHref}
                    aria-label={homeAriaLabel}
                    class="inline-flex items-center gap-2.5 font-display text-[22px] italic tracking-[0.005em] text-fg-primary"
                >
                    <span class="inline-flex h-8 w-8 items-center justify-center">
                        <img
                            src={versionedUrl('/loporrit-mark-192.png')}
                            alt=""
                            width="32"
                            height="32"
                            class="block h-full w-full object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.3)]"
                        />
                    </span>
                    <span>Gposingway</span>
                    {brand?.path?.map((segment, i) => (
                        <span
                            key={i}
                            class="text-fg-muted before:mx-1.5 before:not-italic before:text-fg-faint before:content-['/']"
                        >
                            {segment}
                        </span>
                    ))}
                    {brand?.version && (
                        <span class="ml-1 font-mono text-[10px] not-italic uppercase tracking-[0.18em] text-fg-muted">
                            {brand.version}
                        </span>
                    )}
                </a>

                <div class="min-w-0">{middleSlot}</div>

                <div class="flex items-center gap-4 text-[13px]">{actions}</div>
            </div>
        </nav>
    );
}

/**
 * Pill-style CTA link. Used by the PlatformPills triplet (Browse catalog / Studio / Pack) and
 * any other chrome that wants the platform's "rounded pill, accent-fill when current" style.
 */
export function NavbarPill({
    href,
    current,
    external,
    children,
}: {
    href: string;
    current?: boolean;
    external?: boolean;
    children: ComponentChildren;
}) {
    return (
        <a
            href={href}
            aria-current={current ? 'page' : undefined}
            target={external ? '_blank' : undefined}
            rel={external ? 'noreferrer noopener' : undefined}
            class={`rounded-full border px-3 py-1.5 text-[13px] transition-colors ${
                current
                    ? 'border-accent bg-accent-glow text-accent'
                    : 'border-rule text-fg-primary hover:border-accent-soft hover:bg-accent-glow'
            }`}
        >
            {children}
        </a>
    );
}

/**
 * Underline-style app link. Active link gets an accent-coloured underline.
 */
export function NavbarLink({
    href,
    current,
    children,
}: {
    href: string;
    current?: boolean;
    children: ComponentChildren;
}) {
    return (
        <a
            href={href}
            aria-current={current ? 'page' : undefined}
            class={`relative py-1 transition-colors ${
                current
                    ? 'text-accent after:absolute after:inset-x-0 after:-bottom-0.5 after:h-px after:bg-accent'
                    : 'text-fg-muted hover:text-fg-primary'
            }`}
        >
            {children}
        </a>
    );
}

/**
 * Identity-only chip. Three states:
 *   - loading: subtle dot placeholder so the nav doesn't reflow on mount.
 *   - anonymous: "Sign in" link to /signin with `?return=` set to the current page so the OAuth
 *     handshake bounces the user back where they were.
 *   - authenticated: avatar (pictureUrl, falling back to a circular gradient with the user's
 *     initial) + display name + a trailing sign-out icon button.
 *
 * Admin role does NOT influence this chip; admin access is a separate `<NavbarAdminPill>`
 * affordance. Keeping concerns split: this chip owns identity; the pill owns role-gated access.
 */
export function NavbarUserChip({
    me: meOverride,
    signInLabel,
    signOutReturnUrl = '/',
}: {
    me?: MeState;
    signInLabel?: string;
    signOutReturnUrl?: string;
}) {
    const auto = useMe();
    const me = meOverride ?? auto;
    const t = useTranslation();

    if (me.status === 'loading') {
        return (
            <span aria-hidden="true" class="inline-flex h-5 items-center gap-2 border-l border-rule pl-3">
                <span class="h-1.5 w-1.5 animate-pulse rounded-full bg-fg-faint" />
            </span>
        );
    }

    if (me.status === 'authenticated' && me.user) {
        const user = me.user;
        // The slug claim is stamped on every sign-in by Koan's account
        // linker and surfaced through /me as `user.claims.slug[0]`.
        // Per ADR-0015 + legacy ADR-0030 the navbar avatar/name is the
        // entry to the user's profile (/u/{slug}); /me resolves the
        // slug-less case by bouncing to the account-setup flow.
        const slug = user.claims?.slug?.[0];
        const profileHref = slug ? `/u/${encodeURIComponent(slug)}` : '/me';
        const profileTitle = slug
            ? `@${slug}`
            : (t('auth.signedIn') || 'Open your account');
        return (
            <span class="inline-flex items-center gap-2 border-l border-rule pl-3">
                <a
                    href={profileHref}
                    title={profileTitle}
                    class="inline-flex items-center gap-2 text-fg-primary transition-colors hover:text-accent"
                >
                    <Avatar user={user} />
                    <span class="hidden max-w-[14ch] truncate sm:inline">
                        {user.displayName ?? user.id ?? t('auth.signedIn')}
                    </span>
                </a>
                <button
                    type="button"
                    onClick={() => signOut(signOutReturnUrl)}
                    title={t('auth.signOut') || 'Sign out'}
                    aria-label={t('auth.signOut') || 'Sign out'}
                    class="grid h-6 w-6 place-items-center rounded text-fg-muted transition-colors hover:bg-state-failed/10 hover:text-state-failed"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                </button>
            </span>
        );
    }

    // anonymous + error → sign-in link with ?return= preserving the current page.
    const returnHref = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';
    return (
        <a
            href={`/signin?return=${encodeURIComponent(returnHref)}`}
            class="inline-flex items-center border-l border-rule pl-3 text-fg-muted transition-colors hover:text-fg-primary"
        >
            {signInLabel ?? t('auth.signIn') ?? 'Sign in'}
        </a>
    );
}

/**
 * Role-gated Admin pill. Renders nothing for anonymous / non-admin users; for admins it renders
 * a pill linking to /admin. When the current location is inside /admin/*, the pill picks up the
 * accent-fill current marker so admins know they're already there.
 */
export function NavbarAdminPill({
    me: meOverride,
    label,
}: {
    me?: MeState;
    label?: string;
}) {
    const auto = useMe();
    const me = meOverride ?? auto;
    const t = useTranslation();

    if (me.status !== 'authenticated' || !me.user || !isAdmin(me.user)) return null;

    const isCurrent =
        typeof window !== 'undefined' &&
        (window.location.pathname === '/admin' || window.location.pathname.startsWith('/admin/'));

    return (
        <NavbarPill href="/admin" current={isCurrent}>
            {label ?? t('nav.admin') ?? 'Admin'}
        </NavbarPill>
    );
}

function Avatar({ user }: { user: CurrentUser }) {
    if (user.pictureUrl) {
        return (
            <img
                src={user.pictureUrl}
                alt=""
                class="h-[22px] w-[22px] rounded-full object-cover ring-1 ring-rule"
            />
        );
    }
    const initial =
        user.displayName?.trim().charAt(0).toUpperCase() ??
        user.id?.trim().charAt(0).toUpperCase() ??
        '·';
    return (
        <span
            aria-hidden="true"
            class="inline-flex h-[22px] w-[22px] items-center justify-center rounded-full font-display text-[12px] italic text-accent-ink"
            style="background: radial-gradient(circle at 35% 30%, var(--color-accent), var(--color-accent-soft));"
        >
            {initial}
        </span>
    );
}
